import { useState } from 'react'

const STATUS_COLORS = {
  added: { dot: 'bg-green', label: 'text-green', border: 'border-green/30', bg: 'bg-green/5' },
  deleted: { dot: 'bg-red', label: 'text-red', border: 'border-red/30', bg: 'bg-red/5' },
  modified: { dot: 'bg-yellow', label: 'text-yellow', border: 'border-yellow/30', bg: 'bg-yellow/5' },
}

const STATUS_SYMBOL = { added: '+', deleted: '−', modified: '~' }

function DiffLine({ line }) {
  if (line.startsWith('---') || line.startsWith('+++')) {
    return (
      <div className="px-3 py-px text-text-faint font-mono text-2xs leading-5 select-none">
        {line}
      </div>
    )
  }
  if (line.startsWith('@@')) {
    return (
      <div className="px-3 py-px bg-accent/10 text-accent font-mono text-2xs leading-5">
        {line}
      </div>
    )
  }
  if (line.startsWith('+')) {
    return (
      <div className="px-3 py-px bg-green/10 text-green font-mono text-2xs leading-5 whitespace-pre-wrap break-words">
        {line}
      </div>
    )
  }
  if (line.startsWith('-')) {
    return (
      <div className="px-3 py-px bg-red/10 text-red font-mono text-2xs leading-5 whitespace-pre-wrap break-words">
        {line}
      </div>
    )
  }
  return (
    <div className="px-3 py-px text-text-dim font-mono text-2xs leading-5 whitespace-pre-wrap break-words">
      {line}
    </div>
  )
}

export default function DiffViewer({ diffs }) {
  const [selected, setSelected] = useState(diffs[0]?.path ?? null)

  if (!diffs || diffs.length === 0) {
    return (
      <div className="text-xs text-text-faint text-center py-6 font-mono">
        No changes vs parent snapshot
      </div>
    )
  }

  const activeDiff = diffs.find((d) => d.path === selected)

  return (
    <div className="flex gap-0 border border-border rounded overflow-hidden" style={{ minHeight: '180px', maxHeight: '420px' }}>
      {/* File list */}
      <div className="w-48 flex-shrink-0 border-r border-border bg-surface2 overflow-y-auto">
        {diffs.map((d) => {
          const c = STATUS_COLORS[d.status] ?? STATUS_COLORS.modified
          const isActive = d.path === selected
          return (
            <button
              key={d.path}
              onClick={() => setSelected(d.path)}
              className={`w-full text-left px-2 py-1.5 flex items-start gap-1.5 transition-colors
                ${isActive ? 'bg-accent/15 text-text' : 'hover:bg-surface text-text-dim'}`}
            >
              <span className={`mt-0.5 flex-shrink-0 text-2xs font-mono font-bold ${c.label}`}>
                {STATUS_SYMBOL[d.status]}
              </span>
              <span className="text-2xs font-mono break-all leading-4">{d.path}</span>
            </button>
          )
        })}
      </div>

      {/* Diff panel */}
      <div className="flex-1 overflow-auto bg-ground">
        {activeDiff ? (
          <div>
            <div className={`px-3 py-1.5 text-2xs font-mono border-b border-border flex items-center gap-2 ${STATUS_COLORS[activeDiff.status]?.bg ?? ''}`}>
              <span className={`font-bold ${STATUS_COLORS[activeDiff.status]?.label ?? 'text-text'}`}>
                {STATUS_SYMBOL[activeDiff.status]}
              </span>
              <span className="text-text-dim">{activeDiff.path}</span>
              <span className={`ml-auto text-2xs px-1.5 py-0.5 rounded font-mono ${STATUS_COLORS[activeDiff.status]?.label ?? ''}`}>
                {activeDiff.status}
              </span>
            </div>
            <div className="overflow-x-hidden">
              {(activeDiff.diff ?? '').split('\n').map((line, i) => (
                <DiffLine key={i} line={line} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-xs text-text-faint text-center py-8">Select a file</div>
        )}
      </div>
    </div>
  )
}
